import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ceil, compose, filter,
  getOr, includes, isEmpty,
  map, size,
  toNumber, toString,
} from 'lodash/fp';
import { FormattedMessage, injectIntl } from 'react-intl';
import dayjs from 'dayjs';
import { Fade, Snackbar, Button as MuiButton } from '@material-ui/core';
import { Create } from '@material-ui/icons';

import {
  Button, Grid, Heading,
  Pagination, RowsPerPageFilter, SearchField,
  Spinner, TableFallbackMessage, RetryDialog,
} from '../../common/components';
import * as notificationActions from '../../notification/actions';
import * as writingActions from '../../writing/actions';
import {
  NewsletterTable,
  StatusFilter, YearFilter,
} from '../components';
import * as actions from '../actions';
import messages from '../messages';

const rowsPerPageOptions = map(rowCount => ({
  id: toString(rowCount),
  value: toString(rowCount),
  label: <FormattedMessage {...messages.rowsPerPageFilterLabel} values={{ num: rowCount }} />,
}), [10, 15, 20]);

const S = {
  OutBoxLink: styled(Button)`
    margin-right: 0.5rem;
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
  `,
  WritingLink: styled(Button)``,
  Create: styled(Create)`
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
    color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  Count: styled.data`
    margin-left: 0.125rem;
    color: ${({ theme }) => theme.palette.secondary.main};
  `,
  CenteredGrid: styled(Grid)`
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  RetryButton: styled(MuiButton)`
    color: ${({ theme }) => theme.palette.error.main};
  `,
};

const Newsletters = ({ intl, match }) => {
  const [searchWord, setSearchWord] = useState('');
  const [year, setYear] = useState(() => ({ value: '0', computed: '' }));
  const [status, setStatus] = useState({ value: 'all', computed: '' });
  const [rowsPerPage, setRowsPerPage] = useState('10');
  const [pageNumber, setPageNumber] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [snackbarText, setSnackbarText] = useState('');
  const [openRetrySnackbar, setOpenRetrySnackbar] = useState(false);
  const [selectedDeletionInfo, setSelectedDeletionInfo] = useState({});

  const notification = useSelector(state => state.notification.notification);
  const newsletters = useSelector(state => state.newsletters.newsletters);
  const targets = useSelector(state => state.newsletters.targets);
  const deletionTask = useSelector(state => state.newsletters.deletionTask);
  const newsletter = useSelector(state => state.writing.newsletter);

  const dispatch = useDispatch();

  const filteredNewsletters = useMemo(() => compose(
    filter(n => (
      (searchWord === ''
        || includes(searchWord, n.title)
        || includes(searchWord, n.writer)
        || includes(searchWord, getOr({ issueNumber: '' }, 'number', n).issueNumber))
      && (year.value === '0' || toString(
        dayjs(n.sendAt || n.startAt || n.createdAt.toDate()).year(),
      ) === year.value)
      && (status.value === 'all' || status.value === n.status)
    )),
    getOr([], 'data'),
  )(newsletters), [newsletters, searchWord, year, status]);
  const pageCount = useMemo(
    () => (ceil(size(filteredNewsletters) / rowsPerPage) || 1),
    [filteredNewsletters, rowsPerPage],
  );

  const handleSearchWordChange = useCallback(event => setSearchWord(event.target.value), []);
  const handleSearchWordClear = useCallback(() => setSearchWord(''), []);
  const handleYearChange = useCallback(event => setYear({ value: event.target.value, computed: event.target.value }), []);
  const handleStatusChange = useCallback(event => setStatus({ value: event.target.value, computed: event.target.value }), []);
  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(event.target.value);

    setPageNumber(1);
  }, []);
  const handlePageNumberClick = useCallback(event => setPageNumber(toNumber(event.currentTarget.dataset.pageNumber)), []);
  const fetchNewsletters = useCallback((...args) => dispatch(actions.fetchNewslettersRequest(...args)), [dispatch]);
  const fetchTargetsByNewsletter = useCallback((...args) => dispatch(actions.fetchTargetsByNewsletterRequest(...args)), [dispatch]);
  const resendNewsletterNotification = useCallback((...args) => dispatch(notificationActions.resendNewsletterNotificationRequest(...args)), [dispatch]);
  const initDeletionTask = useCallback((...args) => dispatch(actions.initDeletionTask(...args)), [dispatch]);
  const initTargets = useCallback(() => dispatch(actions.initTargetsByNewsletter()), [dispatch]);
  const deleteNewsletters = useCallback((...args) => {
    setSelectedDeletionInfo(...args);
    dispatch(actions.deleteNewslettersRequest(...args));
  }, [dispatch]);

  useEffect(() => {
    fetchNewsletters({ sid: match.params.sid });
  }, [fetchNewsletters, match.params.sid]);

  useEffect(() => {
    if ((pageNumber > pageCount) && pageCount) {
      setPageNumber(toString(pageCount));
    }
  }, [pageCount, pageNumber]);

  useEffect(() => {
    if (newsletter.data) {
      setOpenSnackbar(true);
      setSnackbarText('가정통신문이 생성되었습니다.');
      dispatch(writingActions.initNewsletter());
    }
  }, [dispatch, newsletter.data]);

  useEffect(() => {
    if (newsletters.error) {
      setOpenErrorDialog(true);
      dispatch(actions.initNewsletters());
    }
  }, [dispatch, newsletters.error]);

  useEffect(() => {
    if (targets.error) {
      setOpenSnackbar(true);
      setSnackbarText('목록을 불러오는데 실패하였습니다.');
      dispatch(actions.clearTargetsByNewsletterError());
    }
  }, [dispatch, targets.error]);

  useEffect(() => {
    if (deletionTask.error) {
      setOpenRetrySnackbar(true);
      dispatch(actions.clearDeletionTaskError());
    }
  }, [dispatch, deletionTask.error]);

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleExitedSnackbar = () => {
    setOpenSnackbar(false);
    setSnackbarText('');
  };

  const handleCloseRetrySnackbar = () => setOpenRetrySnackbar(false);

  const handleExitedRetrySnackbar = () => setOpenRetrySnackbar(false);

  const handleRetry = () => {
    handleCloseRetrySnackbar();
    deleteNewsletters(selectedDeletionInfo);
  };

  const handleCloseErrorDialog = () => setOpenErrorDialog(false);

  return (
    <>
      <Grid container spacing={2} padding={{ top: 24, bottom: 72 }}>
        <Grid
          container
          item
          xs={12}
          margin={{ bottom: 24 }}
        >
          <Heading>
            <FormattedMessage {...messages.newslettersHeading} />
          </Heading>
        </Grid>
        <Grid container item spacing={2} margin={{ bottom: 4 }}>
          <Grid item xs={3}>
            <SearchField
              placeholder={intl.formatMessage(messages.newslettersSearchFieldPlaceholder)}
              value={searchWord}
              onChange={handleSearchWordChange}
              onClear={handleSearchWordClear}
            />
          </Grid>
          <Grid container item xs={5} spacing={2}>
            <Grid item xs={6}>
              <YearFilter newsletters={newsletters} value={year.computed} onChange={handleYearChange} />
            </Grid>
            <Grid item xs={6}>
              <StatusFilter value={status.computed} onChange={handleStatusChange} />
            </Grid>
          </Grid>
          <Grid container item xs={4} justify="flex-end">
            <S.OutBoxLink component={Link} to={`/schools/${match.params.sid}/school-newsletters/outbox`}>
              <FormattedMessage {...messages.newslettersDraftStorageLink} />
              {/* <S.Count value="23">23</S.Count> */}
            </S.OutBoxLink>
            <S.WritingLink
              component={Link}
              to={`/schools/${match.params.sid}/school-newsletters/writing`}
              variant="contained"
              color="primary"
            >
              <S.Create />
              <FormattedMessage {...messages.newslettersFormLink} />
            </S.WritingLink>
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
        >
          {/* TODO: 검색 필터 방식 재논의 예정 */}
          {newsletters && (
            <NewsletterTable
              newsletters={{ loading: newsletters.loading, data: filteredNewsletters, error: null }}
              targets={targets}
              pageNumber={pageNumber}
              rowsPerPage={rowsPerPage}
              searchWord={searchWord}
              deletionTask={deletionTask}
              deleteNewsletters={deleteNewsletters}
              fetchTargetsByNewsletter={fetchTargetsByNewsletter}
              initDeletionTask={initDeletionTask}
              initTargets={initTargets}
              onSend={resendNewsletterNotification}
            />
          )}
        </Grid>
        {!newsletters.loading && !isEmpty(filteredNewsletters) && (
          <Grid
            container
            item
            xs={12}
            justify="space-between"
            margin={{ top: 32 }}
          >
            <S.CenteredGrid item>
              <Pagination
                pageCount={pageCount}
                pageNumber={pageNumber}
                onClick={handlePageNumberClick}
              />
            </S.CenteredGrid>
            <Grid item>
              <RowsPerPageFilter
                options={rowsPerPageOptions}
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              />
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          {!newsletters.loading && isEmpty(filteredNewsletters) && (
            <TableFallbackMessage>
              <FormattedMessage
                {...messages[(searchWord || status.value !== 'all') ? 'newslettersTableFallbackMessageFiltered' : 'newslettersTableFallbackMessageNoData']}
              />
            </TableFallbackMessage>
          )}
        </Grid>
      </Grid>
      {(notification.loading || targets.loading) && <Spinner />}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        onExited={handleExitedSnackbar}
        TransitionComponent={Fade}
        message={snackbarText}
      />
      <Snackbar
        open={openRetrySnackbar}
        autoHideDuration={5000}
        onClose={handleCloseRetrySnackbar}
        onExited={handleExitedRetrySnackbar}
        message="삭제에 실패하였습니다. 다시 시도해주세요."
        action={(
          <S.RetryButton
            size="small"
            onClick={handleRetry}
          >
            재시도
          </S.RetryButton>
        )}
      />
      <RetryDialog open={openErrorDialog} onClose={handleCloseErrorDialog} onRetry={fetchNewsletters}>
        목록을 불러오는데 실패하였습니다. 다시 시도하시겠습니까?
      </RetryDialog>
    </>
  );
};

Newsletters.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default injectIntl(Newsletters);
